<template>
  <BottomSheetModal 
    v-model="isOpen"
    :hideHeader="currentView !== 'main'"
    :hideConfirm="!canManage"
    :confirmDisabled="saving"
    @confirm="handleSave"
    @close="handleClose"
  >
    <template #title>{{ t('priceList') }}</template>

    <div class="views-container">
      <!-- Main View -->
      <div class="main-view" :class="{ 'main-view--hidden': currentView !== 'main' }">

        <div v-if="loading" class="loading-container">
          <v-progress-circular indeterminate size="32" width="3" />
        </div>

        <template v-else>
          <!-- Bank account section -->
          <IosSection form :title="t('priceListPaymentAccount')">
            <IosRow 
              :label="t('bankAccount')"
              :value="bankAccountDisplayValue"
              :clickable="canManage"
              first
              last
              @click="canManage && (currentView = 'bankAccount')"
            />
          </IosSection>

          <!-- System items section -->
          <IosSection form :title="t('priceListSystemItems')">
            <IosRow 
              v-for="(item, index) in visibleSystemItems" 
              :key="item.systemKey"
              :label="t(`priceItem_${item.systemKey}_title`)"
              :first="index === 0"
              :last="index === visibleSystemItems.length - 1"
              :clickable="canManage"
              :class="{ 'system-item--disabled': !item.enabled }"
              @click="canManage && openSystemItemEdit(allItems.indexOf(item))"
            >
              <template #icon>
                <v-icon size="20" :color="item.enabled ? 'primary' : undefined" :style="{ opacity: item.enabled ? 1 : 0.35 }">
                  {{ getSystemIcon(item.systemKey!) }}
                </v-icon>
              </template>
              <template #value>
                <span v-if="item.enabled && item.amount > 0" class="item-amount">
                  {{ item.amount }} {{ t('priceListCurrency') }}
                </span>
              </template>
            </IosRow>
          </IosSection>

          <!-- Custom items section -->
          <IosSection form :title="t('priceListCustomItems')">
            <IosRow 
              v-for="(item, index) in customItems" 
              :key="'custom-' + index"
              :label="item.title || ''"
              :subtitle="item.description || ''"
              :first="index === 0"
              :last="index === customItems.length - 1"
              :clickable="canManage"
              @click="canManage && openCustomItemEdit(index)"
            >
              <template #value>
                <span v-if="item.amount > 0" class="item-amount">
                  {{ item.amount }} {{ t('priceListCurrency') }}
                </span>
              </template>
            </IosRow>
            <IosAddRow 
              v-if="canManage"
              :label="t('priceListAddItem')"
              last
              @click="openCustomItemEdit(-1)"
            />
          </IosSection>

          <!-- Treasurers section -->
          <RoleSection
            :users="treasurers"
            :title="t('treasurers')"
            :role-label="t('treasurer')"
            :add-label="t('addTreasurer')"
            :empty-label="t('noTreasurers')"
            :editable="canManage"
            @add="showInviteModal = true"
            @remove="handleRemoveTreasurer"
            @click-user="openProfile"
          />
        </template>
      </div>

      <!-- System Item Edit Subview -->
      <Transition name="slide">
        <div v-if="currentView === 'systemEdit'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="saveSystemItemEdit">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ editingSystemItem ? t(`priceItem_${editingSystemItem.systemKey}_title`) : '' }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll" v-if="editingSystemItem">
            <div class="edit-item-info">
              <v-icon size="28" color="primary" class="mb-2">{{ getSystemIcon(editingSystemItem.systemKey!) }}</v-icon>
              <div class="edit-item-desc">{{ t(`priceItem_${editingSystemItem.systemKey}_desc`) }}</div>
            </div>

            <IosSection form :title="t('priceListItemAmount')">
              <div class="amount-input-row">
                <input
                  v-model="editAmount"
                  type="text"
                  inputmode="numeric"
                  class="amount-input"
                  :placeholder="t('priceListItemAmountPlaceholder')"
                />
                <span class="amount-currency">{{ t('priceListCurrency') }}</span>
              </div>
            </IosSection>

            <IosSection form>
              <IosSwitch
                v-model="editEnabled"
                :label="editEnabled ? t('active') : t('inactive')"
                last
              />
            </IosSection>

            <div class="edit-actions">
              <v-btn
                variant="flat"
                color="primary"
                block
                rounded="xl"
                @click="saveSystemItemEdit"
              >
                Uložit
              </v-btn>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Custom Item Edit Subview -->
      <Transition name="slide">
        <div v-if="currentView === 'customEdit'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="saveCustomItemEdit">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ editingCustomIndex === -1 ? t('priceListAddItem') : (editTitle || t('priceListItemTitle')) }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form :title="t('priceListItemTitle')">
              <IosInput 
                v-model="editTitle"
                :placeholder="t('priceListItemTitlePlaceholder')"
                :maxlength="50"
                first last
              />
            </IosSection>

            <IosSection form :title="t('priceListItemDesc')">
              <IosInput 
                v-model="editDescription"
                :placeholder="t('priceListItemDescPlaceholder')"
                :maxlength="120"
                first last
              />
            </IosSection>

            <IosSection form :title="t('priceListItemAmount')">
              <div class="amount-input-row">
                <input
                  v-model="editAmount"
                  type="text"
                  inputmode="numeric"
                  class="amount-input"
                  :placeholder="t('priceListItemAmountPlaceholder')"
                />
                <span class="amount-currency">{{ t('priceListCurrency') }}</span>
              </div>
            </IosSection>

            <div class="edit-actions">
              <v-btn
                variant="flat"
                color="primary"
                block
                rounded="xl"
                @click="saveCustomItemEdit"
              >
                Uložit
              </v-btn>
            </div>

            <div v-if="editingCustomIndex >= 0" class="remove-section">
              <v-btn
                variant="text"
                color="error"
                block
                @click="removeCustomItem"
              >
                <v-icon start>mdi-delete-outline</v-icon>
                {{ t('priceListRemoveItem') }}
              </v-btn>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Bank Account Subview -->
      <Transition name="slide">
        <div v-if="currentView === 'bankAccount'" class="subview">
          <BankAccountPicker
            v-model="bankAccountValue"
            @back="currentView = 'main'"
          />
        </div>
      </Transition>
    </div>

    <!-- Invite Persons Modal (treasurer) -->
    <InvitePersonsModal
      v-model="showInviteModal"
      :title="t('addTreasurer')"
      confirm-button-label="Přidat"
      :already-selected="treasurers"
      :entity-id="props.teamId"
      entity-type="team"
      role="treasurers"
      @done="handleAddTreasurers"
    />

    <PlayerCardModal ref="playerCardRef" />
  </BottomSheetModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BottomSheetModal from '@/components/BottomSheetModal.vue';
import BankAccountPicker from '@/components/BankAccountPicker.vue';
import IosSection from '@/components/ios/IosSection.vue';
import IosRow from '@/components/ios/IosRow.vue';
import IosAddRow from '@/components/ios/IosAddRow.vue';
import IosInput from '@/components/ios/IosInput.vue';
import IosSwitch from '@/components/ios/IosSwitch.vue';
import axiosInstance from '@/services/axiosInstance';
import InvitePersonsModal from '@/components/InvitePersonsModal.vue';
import RoleSection from '@/components/Team/RoleSection.vue';
import PlayerCardModal from '@/components/PlayerCardModal.vue';
import { mapUserLite } from '@/components/Team/teamRoles';
import type { UserLite } from '@/components/Team/teamRoles';
import systemPriceItemDefs from '@/data/systemPriceItems';

const { t } = useI18n();

interface PriceListItem {
  _id?: string;
  type: 'system' | 'custom';
  systemKey?: string;
  title?: string;
  description?: string;
  amount: number;
  enabled: boolean;
  order: number;
}


const props = defineProps<{
  modelValue: boolean;
  teamId: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const isOpen = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit('update:modelValue', val),
});

// State
const loading = ref(false);
const saving = ref(false);
const canManage = ref(false);
const currentView = ref<'main' | 'systemEdit' | 'customEdit' | 'bankAccount'>('main');
const allItems = ref<PriceListItem[]>([]);
const treasurers = ref<UserLite[]>([]);
const showInviteModal = ref(false);
const playerCardRef = ref<InstanceType<typeof PlayerCardModal>>();
const bankAccountPrefix = ref('');
const bankAccountNumber = ref('');
const bankCode = ref('');

// Computed: system vs custom items
const systemItems = computed(() => allItems.value.filter(i => i.type === 'system'));
const visibleSystemItems = computed(() => {
  if (canManage.value) return systemItems.value;
  return systemItems.value.filter(i => i.enabled);
});
const customItems = computed(() => allItems.value.filter(i => i.type === 'custom'));


// Bank account v-model for BankAccountPicker
const bankAccountValue = computed({
  get: () => ({
    prefix: bankAccountPrefix.value,
    number: bankAccountNumber.value,
    bankCode: bankCode.value,
  }),
  set: (val: { prefix: string; number: string; bankCode: string }) => {
    bankAccountPrefix.value = val.prefix;
    bankAccountNumber.value = val.number;
    bankCode.value = val.bankCode;
  },
});

const bankAccountDisplayValue = computed(() => {
  if (!bankAccountNumber.value || !bankCode.value) return t('notSet');
  const last = bankAccountNumber.value.slice(-3);
  return `...${last}/${bankCode.value}`;
});

// Get icon for a system item key
const getSystemIcon = (key: string) => {
  const def = systemPriceItemDefs.find(d => d.key === key);
  return def?.icon || 'mdi-currency-usd';
};

// ---------- Editing state ----------
const editingSystemIndex = ref(-1);
const editingSystemItem = ref<PriceListItem | null>(null);
const editingCustomIndex = ref(-1);
const editTitle = ref('');
const editDescription = ref('');
const editAmount = ref('');
const editEnabled = ref(false);

// Sanitize amount to digits only
watch(editAmount, (val) => {
  const sanitized = val.replace(/[^0-9]/g, '');
  if (sanitized !== val) editAmount.value = sanitized;
});

const openSystemItemEdit = (globalIndex: number) => {
  editingSystemIndex.value = globalIndex;
  editingSystemItem.value = { ...allItems.value[globalIndex] };
  editAmount.value = String(allItems.value[globalIndex].amount || '');
  editEnabled.value = allItems.value[globalIndex].enabled;
  currentView.value = 'systemEdit';
};

const saveSystemItemEdit = () => {
  if (editingSystemIndex.value >= 0) {
    allItems.value[editingSystemIndex.value].amount = parseInt(editAmount.value, 10) || 0;
    allItems.value[editingSystemIndex.value].enabled = editEnabled.value;
  }
  currentView.value = 'main';
  persistToServer();
};

const openCustomItemEdit = (customIndex: number) => {
  editingCustomIndex.value = customIndex;
  if (customIndex >= 0) {
    const item = customItems.value[customIndex];
    editTitle.value = item.title || '';
    editDescription.value = item.description || '';
    editAmount.value = String(item.amount || '');
  } else {
    editTitle.value = '';
    editDescription.value = '';
    editAmount.value = '';
  }
  currentView.value = 'customEdit';
};

const saveCustomItemEdit = () => {
  if (!editTitle.value.trim()) {
    currentView.value = 'main';
    return;
  }

  if (editingCustomIndex.value >= 0) {
    // Update existing
    const item = customItems.value[editingCustomIndex.value];
    const globalIndex = allItems.value.indexOf(item);
    if (globalIndex >= 0) {
      allItems.value[globalIndex].title = editTitle.value.trim();
      allItems.value[globalIndex].description = editDescription.value.trim() || undefined;
      allItems.value[globalIndex].amount = parseInt(editAmount.value, 10) || 0;
    }
  } else {
    // Add new
    allItems.value.push({
      type: 'custom',
      title: editTitle.value.trim(),
      description: editDescription.value.trim() || undefined,
      amount: parseInt(editAmount.value, 10) || 0,
      enabled: true,
      order: allItems.value.length,
    });
  }
  currentView.value = 'main';
  persistToServer();
};

const removeCustomItem = () => {
  if (editingCustomIndex.value >= 0) {
    const item = customItems.value[editingCustomIndex.value];
    const globalIndex = allItems.value.indexOf(item);
    if (globalIndex >= 0) {
      allItems.value.splice(globalIndex, 1);
    }
  }
  currentView.value = 'main';
  persistToServer();
};

// ---------- Load / Save ----------
const loadPriceList = async () => {
  if (!props.teamId) return;
  loading.value = true;
  try {
    const { data } = await axiosInstance.get(`/teams/${props.teamId}/price-list`);
    canManage.value = data.canManage;
    allItems.value = data.priceList.items || [];
    const ba = data.priceList.bankAccount;
    bankAccountPrefix.value = ba?.accountPrefix || '';
    bankAccountNumber.value = ba?.accountNumber || '';
    bankCode.value = ba?.bankCode || '';

    // Load treasurers from team
    try {
      const { data: teamData } = await axiosInstance.get(`/teams/${props.teamId}`);
      treasurers.value = (teamData.treasurers || []).map(mapUserLite);
    } catch (e) {
      treasurers.value = [];
    }
  } catch (err) {
    console.error('Failed to load price list:', err);
  } finally {
    loading.value = false;
  }
};

const openProfile = (user: UserLite) => {
  playerCardRef.value?.open(user);
};

// ---------- Treasurer add / remove ----------
const handleAddTreasurers = async (users: UserLite[]) => {
  showInviteModal.value = false;
  for (const user of users) {
    if (treasurers.value.some(t => t._id === user._id)) continue;
    try {
      await axiosInstance.post(`/teams/${props.teamId}/treasurers`, { userId: user._id });
      treasurers.value.push(mapUserLite(user));
    } catch (err) {
      console.error('Failed to add treasurer:', err);
    }
  }
};

const handleRemoveTreasurer = async (index: number, user: UserLite) => {
  try {
    await axiosInstance.delete(`/teams/${props.teamId}/treasurers`, { data: { userId: user._id } });
    treasurers.value.splice(index, 1);
  } catch (err) {
    console.error('Failed to remove treasurer:', err);
  }
};

const handleSave = async () => {
  if (!canManage.value || saving.value) return;
  saving.value = true;
  try {
    const bankAccount = (bankAccountNumber.value && bankCode.value)
      ? {
          accountPrefix: bankAccountPrefix.value,
          accountNumber: bankAccountNumber.value,
          bankCode: bankCode.value,
        }
      : null;

    await axiosInstance.put(`/teams/${props.teamId}/price-list`, {
      items: allItems.value,
      bankAccount,
    });

    isOpen.value = false;
  } catch (err) {
    console.error('Failed to save price list:', err);
  } finally {
    saving.value = false;
  }
};

const persistToServer = async () => {
  if (!canManage.value) return;
  try {
    const bankAccount = (bankAccountNumber.value && bankCode.value)
      ? {
          accountPrefix: bankAccountPrefix.value,
          accountNumber: bankAccountNumber.value,
          bankCode: bankCode.value,
        }
      : null;

    await axiosInstance.put(`/teams/${props.teamId}/price-list`, {
      items: allItems.value,
      bankAccount,
    });
  } catch (err) {
    console.error('Failed to save price list:', err);
  }
};

const handleClose = () => {
  isOpen.value = false;
};

// Load on open
watch(isOpen, (val) => {
  if (val) {
    currentView.value = 'main';
    loadPriceList();
  }
});
</script>

<style lang="scss" scoped>
.views-container {
  position: relative;
  min-height: 200px;
}

.main-view {
  transition: opacity 0.3s ease;
  
  &--hidden {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    inset: 0;
  }
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
}

// Slide transitions
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter-from {
  transform: translateX(100%) !important;
}

.slide-leave-to {
  transform: translateX(100%) !important;
}

.subview {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  max-width: 600px;
  height: 92vh;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-sheetBackground));
  border-radius: 32px 32px 0 0;
  z-index: 10000;
  overflow: hidden;
  transform: translateX(0);
}

.subview-header {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  
  > * {
    pointer-events: auto;
  }
}

.subview-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px;
  padding-top: 70px;
}

.back-btn {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: rgb(var(--v-theme-sheetCloseBtn));
  cursor: pointer;
  transition: all 0.2s ease;
  
  :deep(.v-icon) {
    color: rgb(var(--v-theme-sheetText)) !important;
  }
  
  &:hover {
    filter: brightness(0.92);
  }
  
  &:active {
    transform: scale(0.95);
  }
}

.subview-title {
  flex: 1;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
}

.spacer {
  width: 45px;
}

// Item row
.item-row-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  min-height: 44px;
}

.item-row-left {
  flex: 1;
  min-width: 0;
}

.item-row-title {
  font-size: 1rem;
  color: rgb(var(--v-theme-sheetText));
  
  &--disabled {
    opacity: 0.4;
  }
}

.item-row-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.item-amount {
  font-size: 0.9rem;
  color: rgba(var(--v-theme-sheetText), 0.6);
  white-space: nowrap;
}

.system-item--disabled {
  :deep(.row-label) {
    opacity: 0.4;
  }
}

// Empty state
.empty-custom {
  padding: 16px;
  text-align: center;
  color: rgba(var(--v-theme-sheetText), 0.4);
  font-size: 0.9rem;
}

// Bank account description
.bank-account-desc {
  padding: 0 16px 8px;
  font-size: 0.8rem;
  color: rgba(var(--v-theme-sheetText), 0.4);
}

// Edit subview
.edit-item-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  text-align: center;
}

.edit-item-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  margin-bottom: 4px;
}

.edit-item-desc {
  font-size: 0.85rem;
  color: rgba(var(--v-theme-sheetText), 0.5);
  line-height: 1.4;
}

.amount-input-row {
  display: flex;
  align-items: center;
  padding: 0 16px;
  min-height: 48px;
  gap: 8px;
}

.amount-input {
  flex: 1;
  outline: none;
  font-size: 1.2rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  padding: 12px;
  border-radius: 12px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  border: 1px solid rgba(var(--v-theme-sheetText), 0.12);
  transition: border-color 0.2s;
  text-align: right;
  
  &:focus {
    border-color: rgb(var(--v-theme-primary));
  }
  
  &::placeholder {
    color: rgba(var(--v-theme-sheetText), 0.35);
    font-weight: 400;
  }
}

.amount-currency {
  font-size: 1rem;
  color: rgba(var(--v-theme-sheetText), 0.5);
  font-weight: 500;
  flex-shrink: 0;
}

.edit-actions {
  padding: 24px 0 0;
}

.remove-section {
  padding: 16px 0 0;
}
</style>
